import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, User, Menu, X, Heart, Phone, LogOut, LayoutDashboard, Package, GitCompare, Bell, BellOff, MessageCircle, Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import LanguageSwitcher from './LanguageSwitcher';
import { useAuth } from '../context/AuthContext';
import { useSiteConfig } from '../context/SiteConfigContext';
import { pushSupported, currentSubscription, subscribePush, unsubscribePush } from '../lib/push';
import { CatalogAPI, resolveImage, formatPrice } from '../lib/api';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from './ui/dropdown-menu';

// Static top categories (the full list is fetched in CategoriesGrid)
const NAV_CATS = [
  { id: 'obturation', name: "Matériel d'obturation" },
  { id: 'hygiene', name: 'Hygiène & Stérilisation' },
  { id: 'endodontie', name: 'Endodontie' },
  { id: 'rotatifs', name: 'Instruments rotatifs' },
  { id: 'anesthesiques', name: 'Anesthésiques' },
  { id: 'implantologie', name: 'Implantologie' },
  { id: 'prophylaxie', name: 'Prophylaxie' },
  { id: 'orthodontie', name: 'Orthodontie' },
  { id: 'tenue', name: 'Tenue & Protection' },
];

const Header = ({ cartCount = 0 }) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [cats, setCats] = useState([]);
  useEffect(() => { CatalogAPI.categories().then(setCats).catch(() => {}); }, []);
  const navFamilies = cats.filter((c) => c.level === 1 && c.id !== 'fam_autre').sort((a,b)=>(a.order||0)-(b.order||0));
  const catsOfFamily = (pid) => cats.filter((c) => c.parent_id === pid).sort((a,b)=>(a.order||0)-(b.order||0));
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const [compareCount, setCompareCount] = useState(0);
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [unreadMsgs, setUnreadMsgs] = useState(0);
  React.useEffect(() => {
    if (!user) { setUnreadMsgs(0); return; }
    const fetchUnread = async () => {
      try {
        const token = localStorage.getItem('dz_token');
        const [r1, r2] = await Promise.all([
          fetch((process.env.REACT_APP_BACKEND_URL || '') + '/api/messages/unread-count', { headers: { 'Authorization': 'Bearer ' + token } }),
          fetch((process.env.REACT_APP_BACKEND_URL || '') + '/api/occasion/unread-count', { headers: { 'Authorization': 'Bearer ' + token } }),
        ]);
        let total = 0;
        if (r1.ok) { const d = await r1.json(); total += d.count || 0; }
        if (r2.ok) { const d = await r2.json(); total += d.count || 0; }
        setUnreadMsgs(total);
      } catch {}
    };
    fetchUnread();
    const i = setInterval(fetchUnread, 15000);
    return () => clearInterval(i);
  }, [user]);
  const { config } = useSiteConfig();
  const sitePhone = config['site.contact.phone'];
  const siteTitle = config['site.title'];
  const tagline = config['site.tagline'];
  const customMenu = Array.isArray(config['navigation.menu']) ? config['navigation.menu'] : [];

  useEffect(() => {
    const refresh = () => {
      try { setCompareCount(JSON.parse(localStorage.getItem('dz_compare') || '[]').length); }
      catch { setCompareCount(0); }
    };
    refresh();
    window.addEventListener('compare-changed', refresh);
    window.addEventListener('storage', refresh);
    return () => { window.removeEventListener('compare-changed', refresh); window.removeEventListener('storage', refresh); };
  }, []);

  useEffect(() => {
    if (query.trim().length < 2) { setSuggestions([]); return; }
    const tmr = setTimeout(() => {
      CatalogAPI.products({ q: query.trim(), limit: 6 }).then((res) => {
        setSuggestions(Array.isArray(res) ? res.slice(0, 6) : []);
      }).catch(() => setSuggestions([]));
    }, 250);
    return () => clearTimeout(tmr);
  }, [query]);

  const onSearch = (e) => {
    e.preventDefault();
    if (query.trim()) navigate(`/recherche?q=${encodeURIComponent(query)}`);
  };

  const doLogout = async () => {
    await logout();
    navigate('/');
  };

  // Web Push subscription state for "Activer notifications" menu item
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  useEffect(() => {
    if (!user || !pushSupported()) return;
    currentSubscription().then((s) => setPushOn(!!s)).catch(() => setPushOn(false));
  }, [user]);

  const togglePush = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (pushOn) {
        await unsubscribePush();
        setPushOn(false);
        toast.success('Notifications désactivées');
      } else {
        await subscribePush();
        setPushOn(true);
        toast.success('Notifications activées');
      }
    } catch (e) {
      toast.error(e.message || 'Impossible de modifier les notifications');
    } finally {
      setPushBusy(false);
    }
  };

  return (
    <header className="bg-white border-b border-[#E8E0D0] sticky top-0 z-50">
      <div className="bg-[#1F4D3F] text-[#FAF7F2] text-xs">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 sm:gap-4 min-w-0">
            <a href={`tel:${(sitePhone || '').replace(/\s+/g, '')}`} className="flex items-center gap-1.5 hover:text-white">
              <Phone className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{sitePhone}</span>
            </a>

          </div>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            {customMenu.map((m) => m.label && m.url && (
              <Link key={`${m.label}-${m.url}`} to={m.url} className="hidden sm:inline hover:text-white">{m.label}</Link>
            ))}
            {customMenu.length > 0 && <span className="hidden sm:inline">|</span>}
            {!authLoading && !user && (
              <>
                <Link to="/inscription?role=supplier" className="hover:text-white whitespace-nowrap" data-testid="top-become-supplier">{t('common.become_supplier')}</Link>
                <Link to="/inscription?role=reparateur" className="hover:text-white whitespace-nowrap">Devenir Technicien</Link>
                <span className="hidden sm:inline">|</span>
                <Link to="/inscription" className="hidden sm:inline hover:text-white" data-testid="top-register">{t('common.register')}</Link>
                <span className="hidden sm:inline">|</span>
              </>
            )}
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center gap-3 sm:gap-6">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#1F4D3F] flex items-center justify-center">
            <span className="font-serif text-white text-lg sm:text-xl font-semibold">C</span>
          </div>
          <div className="hidden sm:block">
            <div className="font-serif text-2xl font-semibold text-[#1F4D3F] leading-none">{siteTitle}</div>
            <div className="text-[10px] text-[#7A6F5C] tracking-wider uppercase mt-0.5">{tagline}</div>
          </div>
        </Link>

        <form onSubmit={onSearch} className="flex-1 max-w-2xl relative min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A6F5C] pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setShowSug(true); }}
            onFocus={() => setShowSug(true)}
            onBlur={() => setTimeout(() => setShowSug(false), 200)}
            placeholder={t('common.search_placeholder')}
            className="pl-10 pr-12 sm:pr-28 h-11 bg-[#FAF7F2] border-[#E8E0D0] focus-visible:ring-[#1F4D3F] text-base sm:text-sm"
            data-testid="header-search-input"
          />
          <Button type="submit" data-testid="header-search-submit"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 sm:w-auto sm:px-4 p-0 sm:p-2 bg-[#1F4D3F] hover:bg-[#173B30] text-white"
            aria-label={t('common.search')}>
            <Search className="w-4 h-4 sm:hidden" />
            <span className="hidden sm:inline">{t('common.search')}</span>
          </Button>
          {showSug && suggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E8E0D0] rounded-xl shadow-lg overflow-hidden z-50 max-h-96 overflow-y-auto">
              {suggestions.map((p) => (
                <button key={p.id} type="button"
                  onMouseDown={(e) => { e.preventDefault(); setQuery(''); setShowSug(false); navigate(`/produit/${p.id}`); }}
                  className="w-full flex items-center gap-3 p-2.5 hover:bg-[#FAF7F2] text-left border-b border-[#F5F1EA] last:border-0">
                  <img src={resolveImage(p.image)} alt="" className="w-10 h-10 rounded object-cover bg-[#FAF7F2] flex-shrink-0" onError={(e) => { e.target.style.visibility = 'hidden'; }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#2D2A26] truncate">{p.name}</div>
                    {p.brand && <div className="text-[11px] text-[#9A8F82]">{p.brand}</div>}
                  </div>
                  {p.variants?.length > 0 && (
                    <div className="text-xs font-semibold text-[#1F4D3F] flex-shrink-0">
                      {Math.min(...p.variants.map(v => v.price || Infinity).filter(Number.isFinite)) < Infinity
                        ? formatPrice(Math.min(...p.variants.map(v => v.price || Infinity).filter(Number.isFinite)))
                        : ''}
                    </div>
                  )}
                </button>
              ))}
              <button type="button" onMouseDown={(e) => { e.preventDefault(); onSearch(e); setShowSug(false); }}
                className="w-full p-2.5 text-center text-sm text-[#1F4D3F] font-medium hover:bg-[#FAF7F2] bg-[#FDFCFA]">
                Voir tous les résultats pour "{query}"
              </button>
            </div>
          )}
        </form>

        <div className="flex items-center gap-0 sm:gap-1 flex-shrink-0">
          {user?.role !== 'supplier' && (
            <>
              <Link to="/comparer" className="hidden md:flex flex-col items-center px-3 py-2 rounded-md hover:bg-[#F5F1EA] text-[#2D2A26] relative">
                <GitCompare className="w-5 h-5" />
                <span className="text-[10px] mt-0.5">{t('common.compare')}</span>
                {compareCount > 0 && (
                  <span className="absolute top-1 right-1 bg-[#1F4D3F] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{compareCount}</span>
                )}
              </Link>
              <Link to="/favoris" className="hidden md:flex flex-col items-center px-3 py-2 rounded-md hover:bg-[#F5F1EA] text-[#2D2A26]">
                <Heart className="w-5 h-5" />
                <span className="text-[10px] mt-0.5">{t('common.favorites')}</span>
              </Link>
            </>
          )}

          {user && (
            <Link to="/messages" className="relative flex flex-col items-center px-2 sm:px-3 py-2 rounded-md hover:bg-[#F5F1EA] text-[#2D2A26]" title="Messagerie">
              <MessageCircle className="w-5 h-5" />
              <span className="text-[10px] mt-0.5 hidden sm:inline">Messages</span>
              {unreadMsgs > 0 && (
                <span className="absolute top-0 right-1 bg-[#C75B27] text-white text-[10px] min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center">{unreadMsgs}</span>
              )}
            </Link>
          )}
          {user?.role === 'supplier' && (
            <Link to="/fournisseur" className="hidden sm:flex items-center gap-2 bg-[#1F4D3F] hover:bg-[#173B30] text-white px-4 py-2 rounded-lg font-medium text-sm mr-2 transition-colors">
              <LayoutDashboard className="w-4 h-4" /> Mon Espace Pro
            </Link>
          )}
          {user?.role === 'admin' && (
            <Link to="/admin" className="hidden sm:flex items-center gap-2 bg-[#1F4D3F] hover:bg-[#173B30] text-white px-4 py-2 rounded-lg font-medium text-sm mr-2 transition-colors">
              <LayoutDashboard className="w-4 h-4" /> Administration
            </Link>
          )}
          {authLoading ? (
            <div className="w-9 h-9 rounded-full bg-[#F5F1EA] animate-pulse" />
          ) : user ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="flex flex-col items-center px-2 sm:px-3 py-2 rounded-md hover:bg-[#F5F1EA] text-[#2D2A26] focus:outline-none">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-5 h-5 rounded-full" />
                ) : (
                  <User className="w-5 h-5" />
                )}
                <span className="text-[10px] mt-0.5 max-w-[60px] truncate hidden sm:inline">{user.name?.split(' ')[0] || t('common.account')}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs text-[#7A6F5C]">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user.role === 'admin' && (
                  <DropdownMenuItem onClick={() => navigate('/admin')}><LayoutDashboard className="w-4 h-4 mr-2" /> {t('common.administration')}</DropdownMenuItem>
                )}
                {user.role === 'supplier' && (
                  <DropdownMenuItem onClick={() => navigate('/fournisseur')}><LayoutDashboard className="w-4 h-4 mr-2" /> {t('common.supplier_space')}</DropdownMenuItem>
                )}
                {user.role !== 'supplier' && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/mes-commandes')}><Package className="w-4 h-4 mr-2" /> {t('common.my_orders')}</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/mes-annonces')}><Package className="w-4 h-4 mr-2" /> Mes annonces & messages</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/favoris')}><Heart className="w-4 h-4 mr-2" /> {t('common.favorites')}</DropdownMenuItem>
                  </>
                )}
                {pushSupported() && (
                  <DropdownMenuItem
                    onClick={(e) => { e.preventDefault(); togglePush(); }}
                    disabled={pushBusy}
                    data-testid="toggle-push-notifications">
                    {pushOn ? (
                      <><BellOff className="w-4 h-4 mr-2" /> Désactiver les notifications</>
                    ) : (
                      <><Bell className="w-4 h-4 mr-2" /> Activer les notifications</>
                    )}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout}><LogOut className="w-4 h-4 mr-2" /> {t('common.logout')}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/connexion" className="flex flex-col items-center px-2 sm:px-3 py-2 rounded-md hover:bg-[#F5F1EA] text-[#2D2A26]">
              <User className="w-5 h-5" />
              <span className="text-[10px] mt-0.5 hidden sm:inline">{t('common.login')}</span>
            </Link>
          )}

          {user?.role !== 'supplier' && (
            <Link to="/panier" className="flex flex-col items-center px-2 sm:px-3 py-2 rounded-md hover:bg-[#F5F1EA] text-[#2D2A26] relative">
              <ShoppingCart className="w-5 h-5" />
              <span className="text-[10px] mt-0.5 hidden sm:inline">{t('common.cart')}</span>
              {cartCount > 0 && (
                <span className="absolute top-1 right-1 bg-[#C75B27] text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center">{cartCount}</span>
              )}
            </Link>
          )}
          <button onClick={() => setMenuOpen(!menuOpen)} className="lg:hidden p-2" aria-label="Menu">
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <nav className="hidden lg:block border-t border-[#E8E0D0] bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-1 flex-wrap py-2 text-sm">
            <Link to="/categories" className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#F5F1EA] text-[#1F4D3F] font-medium whitespace-nowrap">
              <Menu className="w-4 h-4" /> {t('common.all_categories')}
            </Link>
            {navFamilies.map((fam) => {
              const famCats = catsOfFamily(fam.id);
              const firstTarget = famCats.length ? famCats[0].id : fam.id;
              return (
                <div key={fam.id} className="relative group">
                  <Link to={`/categorie/${firstTarget}`} className="px-3 py-1.5 rounded-md text-[#2D2A26] hover:bg-[#F5F1EA] whitespace-nowrap inline-block font-medium">
                    {fam.name}
                  </Link>
                  {famCats.length > 0 && (
                    <div className="absolute left-0 top-full z-50 hidden group-hover:block bg-white border border-[#E8E0D0] rounded-xl shadow-lg p-2 min-w-[240px]">
                      {famCats.map((c) => (
                        <Link key={c.id} to={`/categorie/${c.id}`} className="flex items-center justify-between px-3 py-1.5 rounded-md text-sm text-[#2D2A26] hover:bg-[#F5F1EA] hover:text-[#1F4D3F]">
                          <span className="truncate">{c.name}</span>
                          <span className="text-xs text-[#7A6F5C] ml-2">{c.products_count || 0}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <span className="text-[#E8E0D0] px-1">|</span>
            <Link to="/occasion" className="px-3 py-1.5 rounded-md text-[#C75B27] hover:bg-[#FFF3E0] whitespace-nowrap font-medium">
              <Package className="w-4 h-4 inline mr-1" />Occasion
            </Link>
            <Link to="/reparateurs" className="px-3 py-1.5 rounded-md text-[#C75B27] hover:bg-[#FFF3E0] whitespace-nowrap font-medium">
              <Wrench className="w-4 h-4 inline mr-1" />Réparateurs
            </Link>
          </div>
        </div>
      </nav>

      {menuOpen && (
        <div className="lg:hidden border-t border-[#E8E0D0] bg-white">
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col">
            {navFamilies.map((fam) => (
              <div key={fam.id} className="border-b border-[#F5F1EA] py-2">
                <div className="text-sm font-semibold text-[#1F4D3F] mb-1">{fam.name}</div>
                <div className="flex flex-col pl-3">
                  {catsOfFamily(fam.id).map((c) => (
                    <Link key={c.id} to={`/categorie/${c.id}`} onClick={() => setMenuOpen(false)} className="py-1 text-sm text-[#2D2A26]">
                      {c.name}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
            <Link to="/occasion" onClick={() => setMenuOpen(false)} className="py-2 text-sm border-b border-[#F5F1EA] text-[#C75B27] font-medium"><Package className="w-4 h-4 inline mr-1.5" />Matériel d'occasion</Link>
            <Link to="/reparateurs" onClick={() => setMenuOpen(false)} className="py-2 text-sm border-b border-[#F5F1EA] text-[#C75B27] font-medium"><Wrench className="w-4 h-4 inline mr-1" />Réparateurs agréés</Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
